import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PinDialog from "../components/PinDialog";
import SetPinReminder from "../components/SetPinReminder";
import { BubzLogo } from "../components/BubzLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";

const EMOJIS = ["🦁", "🦄", "🚀", "🐻", "🐼", "🦊", "🐸", "🐙", "🦖", "🌈"];
const COLORS = ["#FF5A79", "#FFC02D", "#2AB7CA", "#2EC4B6", "#9B5DE5", "#F15BB5"];

export default function ProfileSelector() {
  const navigate = useNavigate();
  const { user, setActiveKid, settings, loadSettings } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [open, setOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [form, setForm] = useState({ name: "", age: 5, avatar_emoji: "🐻", avatar_color: "#FF5A79" });

  const load = async () => {
    try {
      const { data } = await api.get("/profiles");
      setProfiles(data);
    } catch (e) { console.error("load profiles failed", e); }
  };
  useEffect(() => { load(); }, []);

  // Nudge brand-new parents to set a Parent PIN (once per session).
  useEffect(() => {
    if (settings && settings.pin_set === false && !sessionStorage.getItem("bubz_pin_nudged")) {
      sessionStorage.setItem("bubz_pin_nudged", "1");
      setReminderOpen(true);
    }
  }, [settings]);

  const pick = (kid) => {
    setActiveKid(kid);
    toast.success(`Hi ${kid.name}! Let's go 🎈`);
    navigate("/watch");
  };

  const enterParent = () => {
    setPinOpen(false);
    setActiveKid({ name: "Parent", avatar_emoji: "🛡️", avatar_color: "#2EC4B6", parent: true });
    navigate("/parent");
  };

  const addProfile = async (e) => {
    e.preventDefault();
    try {
      await api.post("/profiles", { ...form, age: Number(form.age) });
      setOpen(false);
      setForm({ name: "", age: 5, avatar_emoji: "🐻", avatar_color: "#FF5A79" });
      toast.success("Profile created! 🎉");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/profiles/${id}`);
    load();
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <div className="max-w-5xl mx-auto px-6 py-16 text-center">
        <div className="flex justify-center"><BubzLogo size="lg" testid="profiles-logo" /></div>
        <h1 className="mt-6 text-3xl sm:text-4xl font-display font-bold text-slate-900">Who's watching?</h1>
        <p className="text-slate-500 mt-2">Pick a profile{user?.name ? `, ${user.name.split(" ")[0]}'s family` : ""}.</p>

        <div className="mt-12 flex flex-wrap justify-center gap-6 sm:gap-8" data-testid="profiles-grid">
          {profiles.map((kid, i) => (
            <motion.button key={kid.profile_id} data-testid={`kid-profile-${i}`}
              initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.07 }}
              whileHover={{ scale: 1.06 }} onClick={() => pick(kid)}
              className="group relative flex flex-col items-center gap-3">
              <div className="h-28 w-28 sm:h-32 sm:w-32 rounded-3xl flex items-center justify-center text-5xl shadow-xl transition-all group-hover:ring-4 ring-offset-2"
                style={{ backgroundColor: kid.avatar_color + "22", boxShadow: `0 12px 30px ${kid.avatar_color}33`, ringColor: kid.avatar_color }}>
                {kid.avatar_emoji}
              </div>
              <div>
                <div className="font-bold text-slate-800">{kid.name}</div>
                <div className="text-xs text-slate-400">Age {kid.age}</div>
                {kid.stickers?.length > 0 && (
                  <div className="text-xs font-bold text-amber-500 mt-0.5">⭐ {kid.stickers.length} stickers</div>
                )}
              </div>
              <span onClick={(e) => remove(kid.profile_id, e)} data-testid={`delete-kid-${i}`}
                className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 bg-white rounded-full p-1.5 shadow border text-slate-400 hover:text-rose-500 transition">
                <Trash2 className="h-3.5 w-3.5" />
              </span>
            </motion.button>
          ))}

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-profile-btn" className="flex flex-col items-center gap-3">
                <div className="h-28 w-28 sm:h-32 sm:w-32 rounded-3xl flex items-center justify-center border-2 border-dashed border-slate-300 text-slate-400 hover:border-[#FF5A79] hover:text-[#FF5A79] transition-all">
                  <Plus className="h-10 w-10" />
                </div>
                <div className="font-bold text-slate-500">Add Kid</div>
              </button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader><DialogTitle className="font-display text-2xl">Add a kid profile</DialogTitle></DialogHeader>
              <form onSubmit={addProfile} className="space-y-4 mt-2">
                <div>
                  <Label>Name</Label>
                  <Input data-testid="kid-name-input" required className="mt-1 rounded-xl h-11" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ella" />
                </div>
                <div>
                  <Label>Age</Label>
                  <Input data-testid="kid-age-input" type="number" min="1" max="17" required className="mt-1 rounded-xl h-11" value={form.age}
                    onChange={(e) => setForm({ ...form, age: e.target.value })} />
                </div>
                <div>
                  <Label>Avatar</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {EMOJIS.map((em) => (
                      <button type="button" key={em} onClick={() => setForm({ ...form, avatar_emoji: em })}
                        className={`h-10 w-10 rounded-xl text-xl ${form.avatar_emoji === em ? "ring-2 ring-[#FF5A79] bg-rose-50" : "bg-slate-100"}`}>{em}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Color</Label>
                  <div className="flex gap-2 mt-2">
                    {COLORS.map((c) => (
                      <button type="button" key={c} onClick={() => setForm({ ...form, avatar_color: c })}
                        className={`h-9 w-9 rounded-full ${form.avatar_color === c ? "ring-2 ring-offset-2 ring-slate-800" : ""}`} style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>
                <Button type="submit" data-testid="save-profile-btn" className="w-full h-11 rounded-xl bg-[#FF5A79] hover:bg-[#e04865] font-bold">Create profile</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <button onClick={() => setPinOpen(true)} data-testid="parent-mode-btn"
          className="mt-14 inline-flex items-center gap-2 rounded-full bg-[#2EC4B6] text-white px-6 py-3 font-bold shadow-lg shadow-teal-500/25 active:scale-95 transition-all">
          <ShieldCheck className="h-5 w-5" /> Parent Zone
        </button>
        <p className="mt-3 text-xs text-slate-400">Shopping, selling &amp; the family feed live here — grown-ups only 🔒</p>
      </div>

      <PinDialog open={pinOpen} onClose={() => setPinOpen(false)} onSuccess={enterParent}
        title="Parent Zone" subtitle="Enter your PIN to access adult features." />
      <SetPinReminder open={reminderOpen} onClose={() => setReminderOpen(false)} onDone={loadSettings} />
    </div>
  );
}
